import type { ReactNode } from "react";
import { isStaticDocumentHref } from "@/utils/staticDocument";
import { Link, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { CaireWordmark, PublicSiteHeader } from "@appcaire/ui";
import type { NavItem } from "@appcaire/shared/public-navigation";
import { useLanguage } from "@/hooks/useLanguage";
import { trackEvent } from "@/lib/analytics";
import { AnalysisCta, analysisButtonClass } from "../marketing/AnalysisCta";
import { LanguageSwitcher } from "../LanguageSwitcher";
import { LanguageLinks } from "./LanguageLinks";
import { getNavConfig, getHeaderSections } from "./navConfig";

export const MainNav = () => {
  const { t } = useTranslation([
    "publicSiteNav",
    "navigation",
    "common",
    "resources",
    "features",
    "services",
  ]);
  const { language } = useLanguage();
  const location = useLocation();
  const config = getNavConfig(
    (key, fallback) => t(key, { defaultValue: fallback }),
    language,
  );
  const sections = getHeaderSections(config);
  const renderLink = (
    item: NavItem,
    className: string,
    onClick?: () => void,
  ) => {
    const content = (
      <>
        <span className="block font-medium">{item.label}</span>
        {item.description && (
          <span className="mt-1 block text-xs leading-relaxed text-slate">
            {item.description}
          </span>
        )}
      </>
    );
    const click = () => {
      if (item.href === config.signupCta.href) {
        trackEvent("cta_click", {
          cta_id: "nav_platform_sign_in",
          cta_label: item.label,
          target: item.href,
          page_path: location.pathname,
        });
      }
      onClick?.();
    };
    if (
      item.target === "_blank" ||
      /^https?:/.test(item.href) ||
      isStaticDocumentHref(item.href)
    )
      return (
        <a
          key={item.href}
          href={item.href}
          target={item.target}
          rel={item.rel}
          className={className}
          onClick={click}
        >
          {content}
        </a>
      );
    return (
      <Link
        key={item.href}
        to={item.href}
        reloadDocument={item.reloadDocument}
        className={className}
        onClick={click}
      >
        {content}
      </Link>
    );
  };
  const brand = <CaireWordmark />;
  const renderHome = (
    href: string,
    content: ReactNode,
    onClick?: () => void,
  ) => (
    <Link
      to={href}
      className="flex shrink-0 items-center gap-3 text-xl font-semibold text-caire-ink focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-caire-action"
      onClick={onClick}
    >
      {content}
    </Link>
  );
  const renderCta = (mobile: boolean, onClick?: () => void) => (
    <AnalysisCta
      locale={language}
      cta="analysis_nav"
      label={
        language === "sv" ? "Prova Caire kostnadsfritt" : "Try Caire for free"
      }
      className={`${analysisButtonClass} ${mobile ? "w-full" : "whitespace-nowrap px-4 py-2.5"}`}
      onClick={onClick}
    />
  );
  const headerLabels = {
    navigation: t("publicNavigation.accessibility.navigation", {
      defaultValue: language === "sv" ? "Huvudnavigation" : "Main navigation",
    }),
    mobileNavigation: t("publicNavigation.accessibility.mobileNavigation", {
      defaultValue: language === "sv" ? "Mobilnavigation" : "Mobile navigation",
    }),
    menu: t("publicNavigation.accessibility.menu", {
      defaultValue: language === "sv" ? "Meny" : "Menu",
    }),
    openMenu: t("publicNavigation.accessibility.openMenu", {
      defaultValue: language === "sv" ? "Öppna meny" : "Open menu",
    }),
    closeMenu: t("publicNavigation.accessibility.closeMenu", {
      defaultValue: language === "sv" ? "Stäng meny" : "Close menu",
    }),
    showSection: t("publicNavigation.accessibility.showSection", {
      defaultValue: language === "sv" ? "Visa" : "Show",
    }),
  };
  return (
    <PublicSiteHeader
      key={`${language}:${location.pathname}`}
      labels={headerLabels}
      homeHref={language === "sv" ? "/" : "/en"}
      sections={sections}
      login={config.signupCta}
      brand={brand}
      renderHome={renderHome}
      renderLink={renderLink}
      renderCta={renderCta}
      localeSwitcher={
        <>
          <span className="hidden lg:inline-flex">
            <LanguageLinks />
          </span>
          <span className="lg:hidden">
            <LanguageSwitcher />
          </span>
        </>
      }
    />
  );
};

export default MainNav;
